import {UserLight} from '@store/settings/users';
import {Pagination} from '@store/common/pagination.model';
import {DocumentInfo} from '@store/files';
import {alpha} from '@mui/material';

import I18n from '@utils/i18n.utils';
import {SelectItem} from '@components/inputs/select/Select.component';
import {Colors} from '@constants/colors.constants';

import {ExecutionStatus} from '@store/operations/executions';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';

export interface IssueKind {
  id: number;
  name: string;
  type: IssueKindType;
  translationKey: string;
}

export enum IssueStatus {
  CREATED = 'CREATED',
  CLOSED = 'CLOSED',
}

export namespace IssueStatus {
  export const selectItems = (): SelectItem[] => [
    {
      label: I18n.t('global.statusEnum.created'),
      value: IssueStatus.CREATED,
      backgroundColor: alpha(Colors.textWithOpacity, 0.5)
    },
    {label: I18n.t('global.statusEnum.closed'), value: IssueStatus.CLOSED, backgroundColor: alpha(Colors.primary, 0.5)},
  ];
}

export enum IssueKindType {
  QUALITY = 'QUALITY',
  SECURITY = 'SECURITY',
  ENVIRONMENT = 'ENVIRONMENT',
  HEALTH = 'HEALTH',
  OTHER = 'OTHER',
}

export namespace IssueKindType {
  export const selectItems = () => [
    {label: I18n.t('global.issueKindType.quality'), value: IssueKindType.QUALITY},
    {label: I18n.t('global.issueKindType.security'), value: IssueKindType.SECURITY},
    {label: I18n.t('global.issueKindType.environment'), value: IssueKindType.ENVIRONMENT},
    {label: I18n.t('global.issueKindType.health'), value: IssueKindType.HEALTH},
    {label: I18n.t('global.issueKindType.other'), value: IssueKindType.OTHER}
  ];
}

export interface ManageIssue {
  phoneNumber?: string;
  id?: number;
  author?: UserLight;
  issueSignature?: string;
  status?: IssueStatus;
  resolution?: string;
  documents?: DocumentInfo[];
  documentsToAdd?: DocumentInfo[];
  documentsToDelete?: DocumentInfo['id'][];
  executionId?: number;
  executionSignature?: string;
  issueKind?: IssueKind;
  reportedAt?: Date;
  dateHour?: Date;
  resolvedDateHour?: Date;
  resolver?: UserLight;
  resolvedAt?: Date;
  description?: string;
  updatedAt?: Date;
  clientOrganisationUnitId?: number;
  supplierOrganisationUnitId?: number;
  organisationType?: string;
  organisationUnit?: number;
  clientOrganisationUnit?: {
    id: number;
    name: string;
    clientOrganisation: { id: number; name: string };

  };
  supplierOrganisationUnit?: {
    id: number;
    name: string;
    supplierOrganisation: { id: number; name: string };
  };
}

export interface IssueDetails {
  id?: number;
  author?: UserLight;
  issueSignature?: string;
  status?: IssueStatus;
  documents?: DocumentInfo[];
  executionSignature?: string;
  issueKind?: IssueKind;
  reportedAt?: Date;
  description?: string;
  updatedAt?: Date;
}

export namespace IssueDetails {
  export const toManageIssue = (issue: IssueDetails): ManageIssue => ({
    ...issue,
    issueKind: issue.issueKind,
    dateHour: issue.reportedAt,
  });
}

export interface IssueLight {
  id: number;
  issueSignature: string;
  author?: UserLight;
  reportedAt: string;
}

export interface IssueStatusHistory {
  status: ExecutionStatus;
  createdAt: Date;
  eventType: string;
  author: UserLight;
  id: number;
}

export interface IssuesGroupedAction {
  status?: IssueStatus;
}

export interface IssuesFilters {
  search: string;
  issueStatus?: IssueStatus;
  from?: Date;
  to?: Date;
  tableFilters: TableFilter[];
}

export namespace IssuesFilters {
  export const initTableFilters = () => TableFilterType.initTableFilters([
    TableFilterType.CLIENT_ORGANISATION,
    TableFilterType.CLIENT_ORGANISATION_UNIT,
    TableFilterType.SUPPLIER_ORGANISATION,
    TableFilterType.SUPPLIER_ORGANISATION_UNIT,
    TableFilterType.WASTE_FAMILY,
    TableFilterType.WASTE,
    TableFilterType.CONTAINER,
    TableFilterType.SERVICE_KIND,
  ]);
}

export interface IssuePagination extends Pagination {
  data: Issue[];
}

export interface IssueUi {
  id: number;
  selected: boolean;
}

export interface Issue {
  id: number;
  reportedAt: Date;
  type: string;
  typeTranslationKey: string;
  comment: string;
  executionId: number;
  executionTicketSignature: string;
  waste: string;
  container: string;
  status: IssueStatus;
  clientOrganisationName?: string;
  clientOrganisationUnitName?: string;
  supplierOrganisationName?: string;
  supplierOrganisationUnitName?: string;
}
